/**
 * GWP PDP Item Custom Element
 * Renders free gift product HTML based on collection membership
 */
class GWPPDPItem extends HTMLElement {
	constructor() {
		super()
		this.retryCount = 0
		this.maxRetries = 50 // 5 seconds max wait time
	}

	connectedCallback() {
		this.render()
	}

	async render() {
		// Wait for freeGiftConfig to be available
		if (typeof freeGiftConfig === 'undefined' || !Array.isArray(freeGiftConfig) || freeGiftConfig.length === 0) {
			// Retry after a short delay, but limit retries
			if (this.retryCount < this.maxRetries) {
				this.retryCount++
				setTimeout(() => this.render(), 100)
				return
			} else {
				console.warn('[GWP PDP] freeGiftConfig not available after maximum retries')
				return
			}
		}

		// Get product data
		const product = this.getProductData()
		if (!product) {
			return
		}

		// Check if product belongs to any GWP collection
		const matchingConfig = this.findMatchingConfig(product, freeGiftConfig)
		// console.log('[GWP PDP] Matching config:', matchingConfig)
		if (!matchingConfig) {
			// No matching collection, don't render anything
			return
		}

		// Render the GWP HTML
		this.renderGWPHTML(matchingConfig)
	}

	getProductData() {
		// Prioritize window.productData (from product-json-data.liquid snippet)
		// This has collections as array of strings (handles)
		if (window.productData) {
			try {
				let productData = window.productData

				// If it's a string, try to clean it first
				if (typeof productData === 'string') {
					// Remove control characters that can break JSON parsing
					productData = productData.replace(/[\x00-\x1F\x7F]/g, '')
					return JSON.parse(productData)
				} else {
					// Already an object, return directly
					return productData
				}
			} catch (e) {
				console.warn('[GWP PDP] Error parsing window.productData:', e)
				// Try to extract JSON from the string if it's embedded
				if (typeof window.productData === 'string') {
					try {
						// Try to find JSON object in the string
						const jsonMatch = window.productData.match(/\{[\s\S]*\}/)
						if (jsonMatch) {
							const cleaned = jsonMatch[0].replace(/[\x00-\x1F\x7F]/g, '')
							return JSON.parse(cleaned)
						}
					} catch (e2) {
						console.warn('[GWP PDP] Failed to extract JSON from window.productData:', e2)
					}
				}
			}
		}

		// Fallback: Try to get from ProductJSON script
		const productScript = document.querySelector('script[type="application/json"][id^="ProductJSON-"]:not([id*="metafields"])')
		if (productScript) {
			try {
				return JSON.parse(productScript.textContent)
			} catch (e) {
				console.warn('[GWP PDP] Error parsing ProductJSON script:', e)
			}
		}

		return null
	}

	findMatchingConfig(product, configArray) {
		// console.log('[GWP PDP] findMatchingConfig - product collections:', product.collections)
		if (!product || !product.collections || !Array.isArray(product.collections)) {
			// console.log('[GWP PDP] Product has no collections or collections is not an array')
			return null
		}

		// Get collection handles from product
		// From product-json-data.liquid, collections are already an array of strings (handles)
		// But we also handle objects in case of fallback to ProductJSON script
		const productCollectionHandles = product.collections.map(c => {
			if (typeof c === 'string') return c
			if (c && typeof c === 'object') {
				if (c.handle) return c.handle
				if (c.id) return String(c.id)
			}
			return null
		}).filter(Boolean)

		// console.log('[GWP PDP] Product collection handles:', productCollectionHandles)

		// Find config item where gwpCollection matches one of product's collections
		for (const configItem of configArray) {
			if (!configItem.gwpCollection) {
				// console.log('[GWP PDP] Config item has no gwpCollection')
				continue
			}

			// console.log('[GWP PDP] Checking config item:', configItem)

			let gwpCollectionHandle = null
			if (typeof configItem.gwpCollection === 'string') {
				gwpCollectionHandle = configItem.gwpCollection
				// console.log('[GWP PDP] gwpCollection is string (handle):', gwpCollectionHandle)
			} else if (configItem.gwpCollection && typeof configItem.gwpCollection === 'object') {
				if (configItem.gwpCollection.handle) {
					gwpCollectionHandle = configItem.gwpCollection.handle
					// console.log('[GWP PDP] gwpCollection.handle:', gwpCollectionHandle)
				} else if (configItem.gwpCollection.id) {
					// If it's an ID, we need to match by ID (for fallback ProductJSON format)
					const gwpCollectionId = String(configItem.gwpCollection.id)
					const hasMatchingId = product.collections.some(c => {
						const cId = typeof c === 'object' && c.id ? String(c.id) : null
						return cId === gwpCollectionId
					})
					if (hasMatchingId) {
						// console.log('[GWP PDP] ✅ Match found by ID:', gwpCollectionId)
						return configItem
					}
					console.log('[GWP PDP] No match by ID, continuing...')
					continue
				}
			}

			// Check if product belongs to this collection by handle
			if (gwpCollectionHandle && productCollectionHandles.includes(gwpCollectionHandle)) {
				// console.log('[GWP PDP] ✅ Match found by handle:', gwpCollectionHandle)
				return configItem
			}

			// console.log('[GWP PDP] No match for this config item')
		}

		console.log('[GWP PDP] No matching config found')
		return null
	}

	renderGWPHTML(config) {
		// Get image URL from config
		let imageUrl = config.freeGiftImage || config.freeGfitImage || ''

		// If no direct image URL, try to get from freeGiftProduct (which might be an array)
		if (!imageUrl && config.freeGiftProduct) {
			try {
				// Handle freeGiftProduct as array, string, or object
				let freeGiftProducts = config.freeGiftProduct

				if (typeof freeGiftProducts === 'string') {
					freeGiftProducts = JSON.parse(freeGiftProducts)
				}

				// Normalize to array
				if (!Array.isArray(freeGiftProducts)) {
					freeGiftProducts = [freeGiftProducts]
				}

				// Use first product in array
				const product = freeGiftProducts[0]

				if (product) {
					// featured_image from product-json-data.liquid is an object with src property
					if (product.featured_image) {
						if (typeof product.featured_image === 'string') {
							imageUrl = product.featured_image
						} else if (product.featured_image.src) {
							imageUrl = product.featured_image.src
						}
					} else if (product.featured_media) {
						if (typeof product.featured_media === 'string') {
							imageUrl = product.featured_media
						} else if (product.featured_media.src) {
							imageUrl = product.featured_media.src
						} else if (product.featured_media.preview_image) {
							imageUrl = product.featured_media.preview_image.src
						}
					} else if (product.images && product.images.length > 0) {
						// images array contains objects with src property
						const firstImage = product.images[0]
						if (typeof firstImage === 'string') {
							imageUrl = firstImage
						} else if (firstImage.src) {
							imageUrl = firstImage.src
						}
					}
				}
			} catch (e) {
				console.warn('[GWP PDP] Error parsing freeGiftProduct:', e)
			}
		}

		// If image is not a full URL, try to construct it
		if (imageUrl && !imageUrl.startsWith('http') && !imageUrl.startsWith('//')) {
			if (imageUrl.startsWith('/')) {
				imageUrl = window.location.origin + imageUrl
			} else {
				imageUrl = 'https:' + imageUrl
			}
		}

		// Fallback image
		if (!imageUrl) {
			imageUrl = '/assets/gwp-image.png'
		}

		// Build HTML matching the structure from _pdp-gwp.liquid (lines 10-24)
		this.innerHTML = `
			<div class="main-pdp__gwp-wrapper">
				<div class="main-pdp__gwp-item">
					<div class="gwp-image-wrapper">
						<img src="${imageUrl}" width="99" height="72" alt="gwp product" loading="lazy">
					</div>
					<div class="gwp-details">
						${config.fgPdpTitle ? `<h3>${this.escapeHtml(config.fgPdpTitle)}</h3>` : ''}
						${config.fgPdpDesc ? `<p>${this.escapeHtml(config.fgPdpDesc)}</p>` : ''}
					</div>
				</div>
			</div>
		`
	}

	escapeHtml(text) {
		const div = document.createElement('div')
		div.textContent = text
		return div.innerHTML
	}
}

// Register the custom element
if (!customElements.get('gwp-pdp-item')) {
	customElements.define('gwp-pdp-item', GWPPDPItem)
}

